import type { WorkflowDefinition } from "../types";

/**
 * W32 — Track a category's AI visibility over time.
 *
 * W2 for a market. Same shape, same five checkpoints, same first move: start
 * the report. A market has no focal brand, so the report does two extra things
 * for it. If the category has no prompts yet it writes a first set and saves
 * them to the Prompts page, and the brands come *out* of the answers rather
 * than being named up front. So the order is report, the brands it found,
 * rounding the prompt set out, then daily tracking and a goal.
 *
 * This is the checklist a new CATEGORY property is put on when it is created
 * (`setupWorkflowIdFor`). W2 keeps every subject that has a focal brand of its
 * own, and W29 keeps the organization-level version for a market that is not a
 * property yet.
 *
 * `shared`: this is *the property's* checklist, so anyone on the team
 * continues the same one.
 */
export const categoryTrackingWorkflow: WorkflowDefinition = {
	id: "category-tracking",
	wNumber: 32,
	title: "Track a category's AI visibility over time",
	outcome:
		"Track a market every day, with a goal attached, so you can show which brands moved and why.",
	audiences: ["pr", "agencies", "inhouse"],
	uses: ["Prompts", "AI Reports", "Brands", "Projects"],
	scope: "property",
	concurrency: "shared",
	progress: "observed",
	// Only a market, which has no focal brand of its own. Every other subject
	// gets W2, which is the same five checkpoints in brand wording.
	isApplicable: (mode) => mode.isCategory,
	// Shares W29's guide: one article covers mapping a market and keeping the
	// map current, and the two playbooks are the same story at two scopes.
	articleSlug: "how-to-map-a-category-in-ai-answers",
	articleStatus: "published",
	status: "live",
	steps: [
		{
			id: "run-report",
			title: "Start the report",
			page: "AI Reports",
			route: "/ai-reports",
			criterion: "You've completed one report.",
			why: "The report is the starting point for a market. It writes a first set of prompts if you have none, then finds the brands AI names.",
			docsHref: "/docs/dashboards/ai-visibility-reports",
			tasks: [
				"Start a report and wait 4 to 15 minutes",
				"Read which brands AI names most often",
				"Note the brands you did not expect to see",
			],
			complete: { kind: "count", key: "completedReportCount", gte: 1 },
			spend: { kind: "aiReport" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Completed reports",
							value: state.property.completedReportCount,
							target: 1,
						}
					: null,
		},
		{
			id: "review-brands",
			title: "Review the brands AI named",
			page: "Brands",
			route: "/competitors",
			criterion: "You've kept at least five brands.",
			why: "The report suggests the brands it found in the answers. Keeping the right ones is what makes share of voice mean something.",
			docsHref: "/docs/dashboards/competitors",
			tasks: [
				"Remove anything that is not a real brand in this market",
				"Add the brands buyers ask about that AI missed",
				"Add short names, nicknames and former names",
			],
			complete: { kind: "count", key: "competitorCount", gte: 5 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Brands tracked",
							value: state.property.competitorCount,
							target: 5,
						}
					: null,
		},
		{
			id: "generate-prompts",
			title: "Round out the prompts buyers ask",
			page: "Prompts",
			route: "/discovery-queries",
			criterion:
				"You've added 15 or more prompts across two buying stages.",
			why: "The report leaves you a starter set. Fifteen or more, across two buying stages, is where the daily ranking settles down.",
			docsHref: "/docs/getting-started/choosing-prompts",
			tasks: [
				"Generate more prompts from the frameworks that fit the market",
				"Cover the problem, the comparison and the shortlist",
				"Tag the set so every dashboard can filter to it",
			],
			// Proxy for the coverage score, as in W2 and W29: computing the
			// real one embeds every prompt, which has no business in a loader.
			// The step panel hydrates the true score lazily.
			complete: {
				kind: "all",
				of: [
					{ kind: "count", key: "activePromptCount", gte: 15 },
					{ kind: "count", key: "promptStageCount", gte: 2 },
				],
			},
			enrichment: "coverageScore",
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Active prompts",
							value: state.property.activePromptCount,
							target: 15,
							caption: `${state.property.promptStageCount} of 3 buying stages covered`,
						}
					: null,
		},
		{
			id: "create-project",
			title: "Set up a project to track prompts daily",
			page: "Projects",
			route: "/projects/new",
			criterion: "You've set up one active project.",
			why: "A single report is one day of a market. A project puts the prompts on a daily schedule and shows the margin of error.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Create a General project named for the market",
				"Add the category prompts to it, then check the Data Accuracy card",
				"Set the start date and any goals",
			],
			complete: { kind: "count", key: "activeProjectCount", gte: 1 },
			spend: { kind: "trackedPrompts", count: "fromState" },
			signal: ({ state }) =>
				state.property
					? {
							kind: "checklist",
							items: [
								{
									label: "Active project",
									done: state.property.activeProjectCount > 0,
								},
								{
									label: "Prompts assigned",
									done: state.property.activePromptCount > 0,
								},
							],
						}
					: null,
		},
		{
			id: "connect-goal",
			title: "Connect a goal to the project",
			page: "Projects",
			route: "/projects",
			criterion: "You've added a goal to an active project.",
			why: "A goal ties the work to the visibility you are measuring. In a market that means a coverage group, a publisher, an article or an author.",
			docsHref: "/docs/dashboards/projects",
			tasks: [
				"Group the coverage that shapes how buyers read this market",
				"Point a goal at that coverage group, a publisher, an article or an author",
				"Open the project and add the goal from the Goals card",
			],
			complete: { kind: "count", key: "activeProjectGoalCount", gte: 1 },
			signal: ({ state }) =>
				state.property
					? {
							kind: "count",
							label: "Goals on active projects",
							value: state.property.activeProjectGoalCount,
							target: 1,
						}
					: null,
		},
	],
};
